// AdminChatContext — exposes the admin chat shell's resume entry point to
// widgets rendered inside the assistant message tree (H.3.4a / H.5 AG-UI).
//
// H.5 Task 11: migrated from manual SSE-drain + AI-SDK setMessages to the
// shell hook's `resumeFrom` — which speaks AG-UI and appends directly to
// the shared `messages` state without any AI-SDK types.
//
// Mirror of apps/web-outreach/src/lib/outreach-chat-context.tsx (PD-4): the
// founder-decision + kyc-decision widgets call useAdminChatContext()
// UNCONDITIONALLY and THROW without a provider. The chat shell wraps its
// message tree in <AdminChatProvider> so every confirmation widget can call
// resumeFromDecision(...) without prop-drilling.
//
// Click flow: founder clicks Approve/Reject (kyc-decision) or Yes/No
// (founder-decision) → resumeFromDecision POSTs to /admin-agent/decision →
// admin-agent runs the confirmed tool (Model Y) and resumes runWithSDK →
// the AG-UI SSE response is consumed by the shell hook and appended as a
// fresh assistant bubble in this chat.
//
// Admin has NO prospect `scope` (dropped vs the outreach context). The
// payload carries the structured KycDecision IDs verbatim so POST /decision
// has everything it needs deterministically.

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import type { SendOptions } from '@rmint/shell';

export interface AdminChatCtx {
  /**
   * POST the founder's click to /admin-agent/decision + drain the resumed
   * AG-UI SSE response into the chat stream as a new assistant bubble.
   * Throws on a pre-stream POST failure (the audit event did NOT land).
   */
  resumeFromDecision(payload: {
    sessionId: string;
    tool_use_id: string;
    action: string;
    subscriptionId?: string;
    investorId?: string;
    rmintId?: string;
    reason?: string;
  }): Promise<void>;
}

export const AdminChatContext = createContext<AdminChatCtx | null>(null);

/**
 * Throws when used outside an AdminChatProvider — the chat shell is the only
 * legitimate provider; absence means a widget was mounted outside the chat
 * tree (or the provider was forgotten in tests).
 */
export function useAdminChatContext(): AdminChatCtx {
  const ctx = useContext(AdminChatContext);
  if (!ctx) {
    throw new Error(
      '[admin-chat-context] AdminChatContext not provided — wrap in AdminChatProvider',
    );
  }
  return ctx;
}

/**
 * Provider — delegates to the shell hook's `resumeFrom` so the resumed
 * AG-UI SSE appends to the same messages state. Takes `resumeFrom` from
 * the Chat component (the useChat result) so every confirmation widget
 * can trigger it without prop-drilling.
 */
export function AdminChatProvider({
  resumeFrom,
  children,
}: {
  resumeFrom: (url: string, body: Record<string, unknown>, opts?: SendOptions) => Promise<void>;
  children: ReactNode;
}) {
  const value = useMemo<AdminChatCtx>(
    () => ({
      resumeFromDecision: async (payload) => {
        await resumeFrom('/admin-agent/decision', payload);
      },
    }),
    [resumeFrom],
  );

  return (
    <AdminChatContext.Provider value={value}>
      {children}
    </AdminChatContext.Provider>
  );
}
